import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ref, onValue, off } from 'firebase/database';
import { database } from './fire-init'; // <-- adjust path if needed
import { Conversation, Message } from '../models/chat.model'; // <-- adjust path

@Injectable({ providedIn: 'root' })
export class CalendarConversationHistoryService {
  private conversationsSubject = new BehaviorSubject<Conversation[]>([]);
  readonly conversations$ = this.conversationsSubject.asObservable();

  private conversationsRef = ref(database, 'conversations');

  constructor() {
    this.startListening();
  }

  /**
   * Start a live subscription to /conversations in the Realtime DB
   * and normalize into Conversation[] with Date objects.
   */
  private startListening(): void {
    onValue(
      this.conversationsRef,
      (snapshot) => {
        const conversations: Conversation[] = [];

        if (snapshot.exists()) {
          const data = snapshot.val() || {};

          Object.keys(data).forEach((key) => {
            const raw = data[key] as any;

            const messages: Message[] = Array.isArray(raw.messages)
              ? raw.messages.map((m: any) => ({
                  ...m,
                  timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
                }))
              : [];

            const conversation: Conversation = {
              id: raw.id ?? key,
              title: raw.title ?? 'Conversation',
              messages,
              createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(),
              updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : new Date(),
              // spread any extra fields (userId, metadata, etc)
              ...raw
            };

            conversations.push(conversation);
          });

          // sort newest first
          conversations.sort(
            (a, b) =>
              new Date(b.updatedAt as any).getTime() -
              new Date(a.updatedAt as any).getTime()
          );
        }

        this.conversationsSubject.next(conversations);
      },
      (error) => {
        console.error('[CalendarConversationHistoryService] Error loading conversations', error);
        this.conversationsSubject.next([]);
      }
    );
  }

  /**
   * Optional clean-up if you ever want to tear this service down manually.
   * Normally Angular keeps it alive for the app lifetime.
   */
  dispose(): void {
    off(this.conversationsRef);
    this.conversationsSubject.complete();
  }
}
